import { Injectable } from '@angular/core';
import { Observable, timer } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';

/**
 * Serializes the three async writers on a job page so an older response can
 * never overwrite a newer write (CodeRabbit round 11):
 *
 * - the normal **save changes** mutation (job.effects `saveChanges`),
 * - the **Ops Summary** auto-save on blur (job-overview) — its metadata-only
 *   response would otherwise replace `job.metadata` mid-normal-save, and the
 *   normal save's full-metadata response (built from a snapshot taken before
 *   the ops write) would otherwise revert the just-saved summary on arrival,
 * - the **Event Notes 📎 uploads** (job-workorders) — an attachment added
 *   while a save mutates would be missing from its snapshot and dropped by
 *   the save-success reload.
 *
 * Writers wait for the gate to clear before incrementing their own counter,
 * so the three locks are held one at a time and no await ordering can
 * deadlock. Counters only matter within one page's lifetime.
 */
@Injectable({ providedIn: 'root' })
export class JobSaveGateService {
    private normalSaves = 0;
    private opsWrites = 0;
    private uploads = 0;

    endNormalSave(): void { this.normalSaves = Math.max(0, this.normalSaves - 1); }
    endOpsWrite(): void { this.opsWrites = Math.max(0, this.opsWrites - 1); }
    endUpload(): void { this.uploads = Math.max(0, this.uploads - 1); }

    private otherBusy(kind: 'normal' | 'ops' | 'upload'): boolean {
        // CodeRabbit round 19: an incoming NORMAL save also waits for an
        // active normal save — the pending-changes snapshot is taken in the
        // buildSaveChangesVariables effect while the previous save's
        // mutation may still be in flight, so two concurrent normal saves
        // could snapshot stale state. (Ops/upload keep their rules below.)
        if (this.normalSaves > 0) { return true; }
        if (kind !== 'ops' && this.opsWrites > 0) { return true; }
        if (kind !== 'upload' && this.uploads > 0) { return true; }
        return false;
    }

    /**
     * Emits once no other kind of write is pending AND atomically claims
     * this kind's slot (CodeRabbit round 15): the visibility check and the
     * counter increment run in the same synchronous emission, so two
     * callers whose `await firstValueFrom(...)` resumes in later microtasks
     * can never both pass an idle gate.
     */
    acquire(kind: 'normal' | 'ops' | 'upload', intervalMs = 100): Observable<void> {
        return timer(0, intervalMs).pipe(
            filter(() => !this.otherBusy(kind)),
            take(1),
            tap(() => {
                if (kind === 'normal') { this.normalSaves++; }
                else if (kind === 'ops') { this.opsWrites++; }
                else { this.uploads++; }
            }),
            map((value) => void value),
        );
    }
}